import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, linkedSignal, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ChatStore } from '@rpgc-ai/store/chat/chat.store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import { EModalName } from '@rpgc-ai/shared-interfaces/enums/modal-name.enum';
import type { IAlternativeMessage, IChatMessage, IModelSettings } from '@rpgc-ai/shared-interfaces/interfaces/chat.interface';
import { ESendType } from '@rpgc-ai/shared-interfaces/interfaces/chat.interface';
import type { IReportBody } from '@rpgc-ai/shared-interfaces/interfaces/complain.interface';
import type { IMessageBodyRequest, IRateMessageBodyRequest } from '@rpgc-ai/shared-interfaces/interfaces/message.interface';
import type { IPersona } from '@rpgc-ai/shared-interfaces/interfaces/persona.interface';

import { TranslocoService } from '@jsverse/transloco';

import { ChatMediaService } from './chat-media.service';
import { QueueService } from './chat-query.service';

import { ModalService } from '../modal/modal.service';
import { NotificationsService } from '../notifications/notifications.service';
import { StorageService } from '../storage/storage.service';
import { LanguageService } from '../translate/language.service';
import { lastUsedModelSettingsAdapter } from '../utils/adapters.util';

@Injectable({ providedIn: 'root' })
export class ChatMessageService {
  // Injections
  readonly #router = inject(Router);

  readonly #languageService = inject(LanguageService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #storageService = inject(StorageService);
  readonly #translocoService = inject(TranslocoService);

  readonly #mediaService = inject(ChatMediaService);
  readonly #queryService = inject(QueueService);
  readonly #modalService = inject(ModalService);

  readonly #chatStore = inject(ChatStore);
  readonly #profileStore = inject(ProfileStore);

  // Variables
  readonly #requestBody = signal<IMessageBodyRequest | null>(null);

  readonly messageIdToRegenerate = signal<string | null>(null);
  readonly messageIdToDelete = signal<string | null>(null);
  readonly messageIdToClone = signal<string | null>(null);
  //edit message
  readonly messageIdToEdit = signal<string | null>(null);
  readonly messageContentToEdit = signal<string | null>(null);

  readonly rateMessageRequestBody = signal<IRateMessageBodyRequest | null>(null);
  readonly reportMessageRequestBody = signal<IReportBody | null>(null);
  readonly isMessageLoading = linkedSignal<boolean>(() => this.messageResource.isLoading());

  readonly persona = computed<IPersona | null>(() => this.#chatStore.persona());

  readonly modelSettings = computed<Partial<IModelSettings> | null>(() => {
    const chat = this.#chatStore.chat();
    const preset = this.#chatStore.lastUsedPreset();

    if (!chat || !preset) {
      return null;
    }

    return lastUsedModelSettingsAdapter(chat, preset);
  });

  /**
   * Ресурс для загрузки чата
   */
  readonly messageResource = httpResource<{ text: string; messageId: string }>(() => {
    const body = this.#requestBody();
    if (!body) {
      return;
    }

    return {
      url: `chats/generate`,
      method: 'POST',
      body,
    };
  });

  readonly deleteMessageResource = httpResource<{ text: string; messageId: string }>(() => {
    const messageIdToDelete = this.messageIdToDelete();
    if (!messageIdToDelete) {
      return;
    }

    return {
      url: `messages/${messageIdToDelete}`,
      method: 'DELETE',
    };
  });

  readonly startNewChatFromMessage = httpResource<{ newChatId: string }>(() => {
    const messageIdToClone = this.messageIdToClone();
    const { id } = untracked(this.#chatStore.chat) ?? {};

    if (!messageIdToClone || !id) {
      return;
    }
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[id] ?? '';

    return {
      url: `chats/${messageIdToClone}/clone`,
      method: 'post',
      body: { chatPassword },
    };
  });

  readonly regeneratedMessageResource = httpResource<{ text: string; messageId: string }>(() => {
    const messageId = this.messageIdToRegenerate();
    const modelSettings = untracked(this.modelSettings);
    const { id, lastUsedModel } = untracked(this.#chatStore.chat) ?? {};

    if (!messageId || !modelSettings || !id || !lastUsedModel) {
      return;
    }
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[id] ?? '';

    return {
      url: `chats/regenerate`,
      method: 'POST',
      body: {
        messageId,
        ...modelSettings,
        model: lastUsedModel.id,
        chatPassword,
      },
    };
  });

  readonly rateMessageResource = httpResource<IChatMessage>(() => {
    const { messageId, rating } = this.rateMessageRequestBody() ?? {};

    if (!messageId || !rating) {
      return;
    }

    return {
      url: `/messages/${messageId}/rating`,
      method: 'PATCH',
      body: rating,
    };
  });

  readonly editMessageResource = httpResource<IChatMessage>(() => {
    const messageId = this.messageIdToEdit();
    const content = this.messageContentToEdit();
    const chat = untracked(this.#chatStore.chat);

    if (!messageId || !content || !chat) {
      return;
    }

    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[chat.id] ?? '';

    return {
      url: `/messages/${messageId}/content`,
      method: 'PATCH',
      body: { content, chatPassword },
    };
  });

  readonly reportMessageResource = httpResource<void>(() => {
    const { id, report } = this.reportMessageRequestBody() ?? {};

    if (!id || !report) {
      return;
    }

    return {
      url: `/messages/${id}/report`,
      method: 'POST',
      body: report,
    };
  });

  constructor() {
    this.#sendNewMessageEffect();
    this.#regenerateMessageEffect();
    this.#deleteMessageEffect();
    this.#startNewChatEffect();
    this.#setMessageRatingEffect();
    this.#editMessageEffect();
    this.#reportMessageEffect();
    this.#checkQueueEffect();
  }

  // Используется в complain.component.ts
  setReportBody(body: IReportBody): void {
    this.reportMessageRequestBody.set(body);
  }

  stopAllPendingRequests(): void {
    this.stopMessageGeneration();

    this.#mediaService.voiceId.set(null);
    this.#mediaService.imgGenerateConfig.set(null);
    this.#mediaService.clearVideoGenerationSettings();
    this.#chatStore.setTextToSpeechMessage(null);
  }

  stopMessageGeneration(): void {
    //drop new message request both continue and simple message
    this.createNewMessageRequest('');
    this.isMessageLoading.set(false);
    this.messageIdToRegenerate.set(null);
    this.messageIdToClone.set(null);
  }

  /**
   * Устанавливает текст нового сообщения
   * @param text Текст нового сообщения
   */
  createNewMessageRequest(text: string): void {
    const messageId = crypto.randomUUID();
    if (!text) {
      this.#requestBody.set(null);
      return;
    }
    this.#queryService.checkPlaceInQueue();

    const chat = this.#chatStore.chat();
    const parentMessageId = chat?.messages.at(-1)?.id;
    const modelSettings = this.modelSettings();

    if (!chat || !parentMessageId || !modelSettings) {
      return;
    }
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[chat.id] ?? '';

    const requestBody: Partial<IMessageBodyRequest> = {
      chatId: chat.id,
      characterId: chat.characterId,
      message: {
        role: 'user',
        content: text,
        id: messageId,
      },
      chatPassword,
      parentMessageId,
      ...modelSettings,
    };

    this.#requestBody.set(requestBody as IMessageBodyRequest);

    const userId = this.#profileStore.profile()?.id;
    const persona = this.persona();

    if (!userId || !persona) {
      return;
    }

    const message = this.#mapMessage(text, messageId, ESendType.USER, parentMessageId, userId, persona);

    if (message) {
      this.#chatStore.sendMessage(message);
    }
  }

  regenerateCurrentMessage(messageId: string): void {
    this.#queryService.checkPlaceInQueue();

    this.messageIdToRegenerate.set(messageId);
  }

  /**
   * Преобразует сообщение в объект IChatMessage
   */
  #mapMessage(
    content: string,
    id: string,
    sendType: ESendType,
    parentMessageId: string | undefined,
    charSendUserId: string | undefined,
    persona: IPersona,
    isMessageEmpty = false,
  ): IChatMessage | undefined {
    if (!charSendUserId || !parentMessageId) {
      return;
    }

    if (!content.length) {
      content = this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.EMPTY_RESPONSE');
      isMessageEmpty = true;
    }

    return {
      alternativeVersions: [],
      content,
      createdAt: new Date().toISOString(),
      id,
      images: [],
      videos: [],
      isBranchStart: true,
      parentMessageId,
      sendType,
      sendUserId: charSendUserId,
      turnId: crypto.randomUUID(),
      versionIndex: 0,
      persona,
      isMessageEmpty,
    };
  }

  #checkQueueEffect(): void {
    const TEN_SEC = 10_000;
    effect(() => {
      const query = this.#queryService.queueData();

      if (!query) {
        return;
      }

      untracked(() => {
        if (query.delayMs < TEN_SEC) {
          this.#queryService.clearQueue();
        } else {
          this.#modalService.openModal(EModalName.CHAT_QUERY);
        }
      });
    });
  }

  #sendNewMessageEffect(): void {
    effect(() => {
      const isLoading = this.messageResource.isLoading();
      const isError = this.messageResource.error();

      untracked(() => {
        const newMessage = this.messageResource.value();
        const persona = this.persona();
        if (isError || isLoading) {
          return;
        }

        if (!newMessage || !persona) {
          return;
        }

        const { messageId, text } = newMessage;

        const chat = this.#chatStore.chat();
        const formattedResponse = this.#mapMessage(
          text,
          messageId,
          ESendType.CHARACTER,
          this.#requestBody()?.message.id,
          chat?.messages.find((m) => m.sendType === ESendType.CHARACTER)?.sendUserId,
          persona,
        );

        if (!chat || !formattedResponse) {
          return;
        }
        this.#chatStore.sendMessage(formattedResponse);
      });
    });
  }

  #regenerateMessageEffect(): void {
    effect(() => {
      const isLoading = this.regeneratedMessageResource.isLoading();
      const isError = this.regeneratedMessageResource.error();

      if (isLoading) {
        return;
      }

      const value = this.regeneratedMessageResource.value();
      const targetId = this.messageIdToRegenerate();

      if (isError || !value || !targetId) {
        this.messageIdToRegenerate.set(null);
        return;
      }

      untracked(() => {
        const chat = this.#chatStore.chat();
        if (!chat) {
          this.messageIdToRegenerate.set(null);
          return;
        }

        const index = chat.messages.findIndex((m) => m.id === targetId);
        if (index === -1) {
          this.messageIdToRegenerate.set(null);
          return;
        }

        const original = chat.messages[index];

        const { messageId, text } = value;

        if (original.isMessageEmpty) {
          this.#chatStore.replaceEmptyMessage(index, messageId, text);
          this.messageIdToRegenerate.set(null);
          return;
        }

        const lastVersion = original.alternativeVersions.reduce((max, item) => Math.max(max, item.versionIndex), 0);

        const alternateVersion: IAlternativeMessage = {
          content: text,
          id: messageId,
          versionIndex: lastVersion + 1,
          images: [],
          videos: [],
          isBranchStart: false,
        };

        this.#chatStore.setRegeneratedMessage(index, alternateVersion);
        this.#chatStore.setRegeneratedMessageActive(index);

        this.messageIdToRegenerate.set(null);
      });
    });
  }

  #deleteMessageEffect(): void {
    effect(() => {
      const isLoading = this.deleteMessageResource.isLoading();
      const isError = this.deleteMessageResource.error();

      if (isLoading) {
        return;
      }

      const hasValue = this.deleteMessageResource.hasValue();
      const target = this.messageIdToDelete();

      if (isError || !hasValue || !target) {
        this.messageIdToDelete.set(null);
        return;
      }

      untracked(() => {
        const { messages = [] } = this.#chatStore.chat() ?? {};
        const index = messages.findIndex(({ id }) => id === target);

        if (index < 0) {
          return;
        }

        this.messageIdToDelete.set(null);
        this.#chatStore.deleteMessage(index);
      });
    });
  }

  #startNewChatEffect(): void {
    effect(() => {
      const isLoading = this.startNewChatFromMessage.isLoading();
      const isError = this.startNewChatFromMessage.error();

      if (isLoading) {
        return;
      }

      if (isError) {
        this.#notificationsService.addErrorNotification(this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.ENCRYPTION_LIMIT'));
        this.messageIdToClone.set(null);
        return;
      }

      const { newChatId } = this.startNewChatFromMessage.value() || {};
      untracked(() => {
        if (!newChatId) {
          return;
        }

        const lang = this.#languageService.getCurrentLanguage();
        this.messageIdToClone.set(null);
        this.stopAllPendingRequests();
        this.#router.navigate([lang, 'chat', newChatId]);
        this.#notificationsService.addSuccessNotification(this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.NEW_CHAT_STARTED'));
      });
    });
  }

  #editMessageEffect(): void {
    effect(() => {
      const isLoading = this.editMessageResource.isLoading();
      const isError = this.editMessageResource.error();

      if (isLoading || !this.messageContentToEdit()) {
        return;
      }

      const value = this.editMessageResource.value();
      const target = this.messageIdToEdit();

      untracked(() => {
        if (isError || !value || !target) {
          this.messageIdToEdit.set(null);
          this.messageContentToEdit.set(null);
          return;
        }

        this.#chatStore.patchMessage(target, { content: this.messageContentToEdit()! });

        this.messageIdToEdit.set(null);
        this.messageContentToEdit.set(null);
      });
    });
  }

  #setMessageRatingEffect(): void {
    effect(() => {
      const isError = this.rateMessageResource.error();
      const isLoading = this.rateMessageResource.isLoading();

      if (!isLoading && !isError) {
        untracked(() => {
          try {
            const { messageId, rating } = this.rateMessageRequestBody() || {};
            if (!messageId || !rating) {
              return;
            }

            this.rateMessageRequestBody.set(null);
            this.#chatStore.patchMessage(messageId, { rating });
          } catch (error) {
            this.#notificationsService.addErrorNotification(this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.ERROR'));
          }
        });
      }
    });
  }

  /**
   * Отправка жалобы на сообщение
   */
  #reportMessageEffect(): void {
    effect(() => {
      const isError = this.reportMessageResource.error();
      const isLoading = this.reportMessageResource.isLoading();

      if (!isLoading && !isError) {
        untracked(() => {
          try {
            const { id, report } = this.reportMessageRequestBody() || {};
            if (!id || !report) {
              return;
            }
            this.reportMessageRequestBody.set(null);
            this.#notificationsService.addSuccessNotification(this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.REPORT_MESSAGE_SENT'));
          } catch (error) {
            this.#notificationsService.addErrorNotification(this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.ERROR'));
          }
        });
      }
    });
  }
}
